import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_INACTIVITY_MS = 15 * 60 * 1000;

/**
 * Idle-inactivity timer hook for the screen saver.
 *
 * - Arms a setTimeout for `inactivityMs` on every remote key press.
 * - When the timer fires, `isVisible` becomes true.
 * - While video is actively playing (signalled by the player via custom window
 *   events), the timer is suspended so the screensaver never interrupts playback.
 * - Any key press while the screensaver is visible dismisses it and restores focus
 *   to whatever had it before (key event is stopped from propagating so the press
 *   does not accidentally trigger a navigation action behind the overlay).
 */
export function useScreenSaverTimer(inactivityMs: number = DEFAULT_INACTIVITY_MS): {
  isVisible: boolean;
} {
  const [isVisible, setIsVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isVideoPlayingRef = useRef(false);
  // true while the Firebolt lifecycle is in the foreground state.
  // Starts true so the timer arms on mount during normal startup; the lifecycle
  // manager will dispatch app:inactive / app:pauseMedia if the app is actually
  // not in the foreground.
  const isAppActiveRef = useRef(true);
  // Updated synchronously at every setIsVisible call site so the keydown handler
  // always reads the correct value without waiting for React to flush the state
  // update (avoids a race window where the overlay is rendered but the ref is stale).
  const isVisibleRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const armTimer = useCallback(() => {
    clearTimer();
    // Do not arm while video is playing or app is not in the foreground.
    if (isVideoPlayingRef.current || !isAppActiveRef.current) return;
    timerRef.current = setTimeout(() => {
      isVisibleRef.current = true;
      setIsVisible(true);
    }, inactivityMs);
  }, [inactivityMs, clearTimer]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isVisibleRef.current) {
        // Dismiss screensaver.
        // preventDefault: stops browser-native back-navigation on TV browsers that
        // map Esc/Back to history.back().
        // stopImmediatePropagation: stops spatial-nav and all other window listeners
        // from acting on this key while the overlay is visible.
        event.preventDefault();
        event.stopImmediatePropagation();
        isVisibleRef.current = false;
        setIsVisible(false);
        armTimer();
      } else {
        armTimer();
      }
    };

    const handleVideoPlaying = () => {
      isVideoPlayingRef.current = true;
      clearTimer();
      // If the screensaver became visible just as playback started, hide it.
      if (isVisibleRef.current) {
        isVisibleRef.current = false;
        setIsVisible(false);
      }
    };

    const handleVideoStopped = () => {
      isVideoPlayingRef.current = false;
      armTimer();
    };

    // Firebolt lifecycle guards — suspend the screensaver whenever the app is
    // not in the foreground. Both background (app:background) and platform-inactive
    // (app:inactive) are treated identically: clear the timer and hide any visible
    // overlay. app:active (foreground) re-arms the timer.
    const handleAppSuspend = () => {
      isAppActiveRef.current = false;
      clearTimer();
      if (isVisibleRef.current) {
        isVisibleRef.current = false;
        setIsVisible(false);
      }
    };

    const handleAppActive = () => {
      isAppActiveRef.current = true;
      armTimer();
    };

    // Use capture phase so we intercept keys before spatial-navigation handlers.
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    window.addEventListener('screensaver:videoPlaying', handleVideoPlaying);
    window.addEventListener('screensaver:videoStopped', handleVideoStopped);
    window.addEventListener('app:pauseMedia', handleAppSuspend);
    window.addEventListener('app:background', handleAppSuspend);
    window.addEventListener('app:inactive', handleAppSuspend);
    window.addEventListener('app:active', handleAppActive);

    // Arm on mount.
    armTimer();

    return () => {
      clearTimer();
      window.removeEventListener('keydown', handleKeyDown, { capture: true });
      window.removeEventListener('screensaver:videoPlaying', handleVideoPlaying);
      window.removeEventListener('screensaver:videoStopped', handleVideoStopped);
      window.removeEventListener('app:pauseMedia', handleAppSuspend);
      window.removeEventListener('app:background', handleAppSuspend);
      window.removeEventListener('app:inactive', handleAppSuspend);
      window.removeEventListener('app:active', handleAppActive);
    };
  }, [armTimer, clearTimer]);

  return { isVisible };
}
